import React, { useState, useEffect } from 'react'
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom'
import {
  Box,
  Button,
  Container,
  Typography,
  Card,
  CardContent,
  Stack,
  Alert,
  IconButton,
  Modal,
  Backdrop,
  Fade,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Divider,
  LinearProgress,
  Chip,
  CircularProgress
} from '@material-ui/core'
import { Icon } from '@iconify/react'
import CloseIcon from '@mui/icons-material/Close'
import { makeStyles } from '@material-ui/styles'
import Page from '../components/Page'
import userApi from 'utils/api/users'
import authApi from 'utils/api/auth'
import { useSnackbar } from 'notistack5'
import storage from 'utils/storage'

const useStyles = makeStyles({
  style: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 400,
    bgcolor: 'background.paper',
    border: '2px solid #000',
    boxShadow: 24,
    p: 4,
  },
})

export default function ArchivedGate({ userData, onClose }) {
  const classes = useStyles()
  const navigate = useNavigate()
  const location = useLocation()
  const { enqueueSnackbar } = useSnackbar()
  const [user, setUser] = useState(userData || location.state?.userData)
  const [payrolls, setPayrolls] = useState([])
  const [selectedPayroll, setSelectedPayroll] = useState('')
  const [urlPayslip, setUrlPayslip] = useState('')
  const [openPayslip, setOpenPayslip] = useState(false)
  const [isLoading, setLoading] = useState(false)
  const [payslipLoading, setPayslipLoading] = useState(false)

  useEffect(() => {
    // If we don't have user data but have a userId in location state, fetch user data
    if (!user && location.state?.userId) {
      fetchUserData(location.state.userId)
    } else if (user && user.role === 0 && user.company && user.isArchived) {
      // Only load payrolls if user is role 0 (employee) AND has company data AND is archived
      // This will be triggered when user data is provided by SessionAuth
      loadPayrolls()
    }
  }, [user, location.state])


  const fetchUserData = async (userId) => {
    try {
      setLoading(true)

      // Use the authenticated endpoint to get user data
      const result = await userApi.get_user_archived(userId)
      if (result && result.data) {
        setUser({ ...result.data, isArchived: true })
      } else {
        // Fallback: create minimal user object from location state
        setUser({
          _id: userId,
          isArchived: true,
          role: location.state?.role ?? 0,
          displayName: 'Archived User',
          company: location.state?.company || 'Unknown Company'
        })
      }
    } catch (error) {
      console.log('User data fetch failed:', error.message)
      // Fallback: create minimal user object from location state
      setUser({
        _id: userId,
        isArchived: true,
        role: location.state?.role ?? 0,
        displayName: 'Archived User',
        company: location.state?.company || 'Unknown Company'
      })
    } finally {
      setLoading(false)
    }
  }

  const loadPayrolls = async () => {
    try {
      setPayslipLoading(true)
      // Check if user has required properties for payroll loading
      if (!user || !user._id || !user.company) {
        console.log('User data incomplete for payroll loading:', user)
        enqueueSnackbar('User data incomplete for payroll loading', { variant: 'error' })
        return
      }

      enqueueSnackbar('Getting payslip data please wait...', { variant: 'info' })

      const result = await userApi.get_payslip_info({ id: user._id, store: user.company })
      if (result.data.record.data && result.data.record.data.length > 0) {
        const payrollData = result.data.record.data.map(data => ({
          id: data.id,
          from: data.payroll_from,
          to: data.payroll_to
        }))
        setPayrolls(payrollData)

        // Open the payslip modal after loading data (similar to AccountPopover)
        setOpenPayslip(true)
        enqueueSnackbar('Payslip data loaded successfully', { variant: 'success' })
      } else {
        setPayrolls([])
        setOpenPayslip(true)
        enqueueSnackbar('No payslip data found', { variant: 'warning' })
      }
    } catch (error) {
      console.error('Error loading payrolls:', error)
      enqueueSnackbar('Error loading payslip data. Please try again.', { variant: 'error' })
    } finally {
      setPayslipLoading(false)
    }
  }

  const handleGeneratePayslip = () => {
    if (!selectedPayroll) {
      enqueueSnackbar('Please select a payroll period', { variant: 'warning' })
      return
    }
    if (!user || !user._id) {
      enqueueSnackbar('User data not available', { variant: 'error' })
      return
    }
    setPayslipLoading(true)
    setUrlPayslip(`https://payroll-live.sparkletimekeeping.com/payroll/public/payslip-api/${user._id}/${selectedPayroll}`)
    setPayslipLoading(false)
  }

  const handleLogout = async () => {
    await storage.remove()
    navigate('/login')
  }

  const handleClosePayslip = () => {
    setOpenPayslip(false)
    setSelectedPayroll('')
    setUrlPayslip('')
  }

  return (
    <Page title="Account Archived | Time In">
      <Container maxWidth="sm" sx={{ py: 5 }}>
        <Card sx={{
          borderRadius: '16px',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
          background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)'
        }}>
          <CardContent sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h2" component="h1" gutterBottom sx={{
              color: '#2FB7DE',
              fontWeight: 'bold',
              mb: 3
            }}>
              Account Archived
            </Typography>

            <Alert severity="warning" sx={{
              mb: 3,
              borderRadius: '8px',
              '& .MuiAlert-icon': {
                color: '#FF9800'
              }
            }}>
              <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                Your account has been archived. You can no longer access the main application.
              </Typography>
            </Alert>

            {user && user.role === 0 ? (
                <Alert severity="info" sx={{
                  mb: 3,
                  borderRadius: '8px',
                  backgroundColor: 'rgba(47, 183, 222, 0.1)',
                  '& .MuiAlert-icon': {
                    color: '#2FB7DE'
                  }
                }}>
                  <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                    As an employee, you can still view your payslips. Please contact your administrator for account restoration.
                  </Typography>
                </Alert>
              ) : (
                <Typography variant="body1" color="text.secondary" sx={{ mb: 3, fontWeight: 'medium' }}>
                  Please contact your administrator to restore your account.
                </Typography>
              )}

              <Stack spacing={2}>
                {user && user.role === 0 && user.isArchived && (
                <Button
                  variant="contained"
                  size="large"
                  onClick={loadPayrolls}
                  disabled={payslipLoading}
                  sx={{
                    borderRadius: '8px',
                    backgroundColor: '#2FB7DE',
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: '1rem',
                    padding: '12px 24px',
                    textTransform: 'none',
                    '&:hover': {
                      backgroundColor: '#23A6DC',
                    },
                    '&:disabled': {
                      backgroundColor: '#ccc',
                    },
                  }}
                >
                  {payslipLoading ? 'Loading...' : 'View Payslips'}
                </Button>
              )}

              <Button
                variant="outlined"
                size="large"
                onClick={handleLogout}
                sx={{
                  borderRadius: '8px',
                  borderColor: '#2FB7DE',
                  color: '#2FB7DE',
                  fontWeight: 'bold',
                  fontSize: '1rem',
                  padding: '12px 24px',
                  textTransform: 'none',
                  '&:hover': {
                    borderColor: '#23A6DC',
                    backgroundColor: 'rgba(47, 183, 222, 0.1)',
                  },
                }}
              >
                Logout
              </Button>
            </Stack>
          </CardContent>
        </Card>

        {/* Payslip Modal */}
        <Modal
          disableScrollLock={true}
          aria-labelledby="payslip-modal-title"
          aria-describedby="payslip-modal-description"
          open={openPayslip}
          onClose={handleClosePayslip}
          BackdropComponent={Backdrop}
          BackdropProps={{
            timeout: 500,
          }}
        >
          <Fade in={openPayslip}>
            <Box sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '90%',
              maxWidth: '900px',
              maxHeight: '90vh',
              overflow: 'auto',
              bgcolor: 'background.paper',
              borderRadius: 3,
              boxShadow: 24,
              p: 0,
            }}>
              <Paper elevation={3} sx={{ borderRadius: 3 }}>
                {/* Header */}
                <Box sx={{
                  p: 4,
                  background: 'linear-gradient(135deg, #2FB7DE 0%, #23A6DC 100%)',
                  color: 'white',
                  borderRadius: '12px 12px 0 0'
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Icon icon="fluent:receipt-money-20-regular" style={{ fontSize: '2.5rem' }} />
                      <Box>
                        <Typography variant="h3" component="h2" sx={{ fontWeight: 'bold', m: 0 }}>
                          Payslip Viewer
                        </Typography>
                        <Typography variant="body1" sx={{ mt: 0.5, opacity: 0.9 }}>
                          Professional payroll document viewer
                        </Typography>
                      </Box>
                    </Box>
                    <Chip
                      label={user?.role === 0 ? 'Employee Access' : 'Restricted Access'}
                      color={user?.role === 0 ? 'success' : 'warning'}
                      variant="filled"
                      sx={{
                        fontWeight: 'bold',
                        fontSize: '0.875rem',
                        py: 1
                      }}
                    />
                  </Box>
                </Box>

                {/* Content */}
                <Box sx={{ p: 4 }}>
                  <IconButton
                    aria-label="close"
                    onClick={handleClosePayslip}
                    sx={{
                      position: 'absolute',
                      right: 20,
                      top: 20,
                      color: (theme) => theme.palette.grey[500],
                      backgroundColor: 'rgba(255, 255, 255, 0.9)',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 1)',
                      },
                      zIndex: 10,
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
                    }}
                  >
                    <CloseIcon />
                  </IconButton>

                  {/* Loading State */}
                  {payslipLoading && (
                    <Box sx={{ mb: 4 }}>
                      <Typography variant="body1" color="text.secondary" sx={{ mb: 2, textAlign: 'center' }}>
                        Loading payslip data...
                      </Typography>
                      <LinearProgress sx={{ borderRadius: 1 }} />
                    </Box>
                  )}

                  {/* Payroll Selection */}
                  <Box sx={{ mb: 4 }}>
                    <Typography variant="h5" sx={{ mb: 3, fontWeight: 'bold', color: '#2FB7DE' }}>
                      📅 Select Payroll Period
                    </Typography>
                    <FormControl fullWidth variant="outlined" size="large">
                      <InputLabel id="payroll-select-label">Choose a payroll period</InputLabel>
                      <Select
                        labelId="payroll-select-label"
                        id="payroll-select"
                        value={selectedPayroll}
                        label="Choose a payroll period"
                        onChange={(e) => setSelectedPayroll(e.target.value)}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            backgroundColor: '#f8f9fa',
                            '&:hover': {
                              backgroundColor: '#f0f2f5',
                            }
                          }
                        }}
                      >
                        {payrolls.length > 0 ? (
                          payrolls.map((payroll) => (
                            <MenuItem key={payroll.id} value={payroll.id}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                                  📊 <b>{payroll.from}</b> - <b>{payroll.to}</b>
                                </Typography>
                              </Box>
                            </MenuItem>
                          ))
                        ) : (
                          <MenuItem disabled>
                            <Typography variant="body2" color="text.secondary">
                              📭 No payslips available for this period
                            </Typography>
                          </MenuItem>
                        )}
                      </Select>
                    </FormControl>
                  </Box>

                  <Divider sx={{ my: 4, borderColor: '#e0e7ff' }} />

                  {/* Generate Button */}
                  <Button
                    variant="contained"
                    color="primary"
                    fullWidth
                    size="large"
                    onClick={handleGeneratePayslip}
                    disabled={!selectedPayroll || payslipLoading}
                    sx={{
                      mb: 4,
                      py: 2,
                      borderRadius: 3,
                      fontSize: '1.1rem',
                      fontWeight: 'bold',
                      textTransform: 'none',
                      boxShadow: '0 6px 20px rgba(47, 183, 222, 0.3)',
                      background: 'linear-gradient(135deg, #2FB7DE 0%, #23A6DC 100%)',
                      '&:hover': {
                        boxShadow: '0 8px 25px rgba(47, 183, 222, 0.4)',
                        transform: 'translateY(-1px)',
                      },
                      '&:disabled': {
                        backgroundColor: 'rgba(47, 183, 222, 0.6)',
                        color: 'rgba(255, 255, 255, 0.7)',
                        transform: 'none',
                      },
                      transition: 'all 0.2s ease-in-out'
                    }}
                  >
                    {payslipLoading ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <CircularProgress size={24} color="inherit" />
                        <span>Generating Payslip...</span>
                      </Box>
                    ) : (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Icon icon="mdi:file-document-outline" style={{ fontSize: '1.5rem' }} />
                        <span>Generate & View Payslip</span>
                      </Box>
                    )}
                  </Button>

                  {/* Payslip Viewer */}
                  {urlPayslip && (
                    <>
                      <Divider sx={{ my: 4, borderColor: '#e0e7ff' }} />
                      <Box sx={{
                        mb: 4,
                        border: '3px solid #2FB7DE',
                        borderRadius: 3,
                        overflow: 'hidden',
                        backgroundColor: '#f8f9fa',
                        boxShadow: '0 4px 20px rgba(47, 183, 222, 0.1)'
                      }}>
                        <Box sx={{
                          p: 3,
                          background: 'linear-gradient(135deg, #2FB7DE 0%, #23A6DC 100%)',
                          color: 'white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between'
                        }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Icon icon="mdi:file-document-outline" style={{ fontSize: '2rem' }} />
                            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                              📄 Payslip Preview
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Chip
                              label="PDF Document"
                              size="medium"
                              sx={{
                                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                                color: 'white',
                                fontWeight: 'bold'
                              }}
                            />
                            <Chip
                              label="High Quality"
                              size="medium"
                              sx={{
                                backgroundColor: 'rgba(255, 255, 255, 0.15)',
                                color: 'white'
                              }}
                            />
                          </Box>
                        </Box>
                        <Box sx={{
                          height: '600px',
                          overflow: 'hidden',
                          backgroundColor: 'white'
                        }}>
                          <iframe
                            src={urlPayslip}
                            style={{
                              width: '100%',
                              height: '100%',
                              border: 'none',
                              zoom: '100%'
                            }}
                            title="Payslip Preview"
                          />
                        </Box>
                      </Box>
                    </>
                  )}

                  {/* Action Buttons */}
                  <Box sx={{
                    display: 'flex',
                    gap: 3,
                    justifyContent: 'flex-end',
                    pt: 3,
                    borderTop: '1px solid #e0e7ff'
                  }}>
                    <Button
                      variant="outlined"
                      onClick={handleClosePayslip}
                      size="large"
                      sx={{
                        borderRadius: 2,
                        px: 4,
                        py: 1.5,
                        textTransform: 'none',
                        fontWeight: 'bold',
                        borderColor: '#2FB7DE',
                        color: '#2FB7DE',
                        '&:hover': {
                          borderColor: '#23A6DC',
                          backgroundColor: 'rgba(47, 183, 222, 0.04)',
                        }
                      }}
                    >
                      Close
                    </Button>
                    {urlPayslip && (
                      <Button
                        variant="contained"
                        color="success"
                        size="large"
                        onClick={() => {
                          const link = document.createElement('a');
                          link.href = urlPayslip;
                          link.download = `payslip-${selectedPayroll}.pdf`;
                          link.click();
                        }}
                        sx={{
                          borderRadius: 2,
                          px: 4,
                          py: 1.5,
                          textTransform: 'none',
                          fontWeight: 'bold',
                          boxShadow: '0 6px 20px rgba(76, 175, 80, 0.3)',
                          background: 'linear-gradient(135deg, #4caf50 0%, #45a049 100%)',
                          '&:hover': {
                            boxShadow: '0 8px 25px rgba(76, 175, 80, 0.4)',
                            transform: 'translateY(-1px)',
                          }
                        }}
                      >
                        <Icon icon="mdi:download" style={{ marginRight: '8px', fontSize: '1.2rem' }} />
                        Download PDF
                      </Button>
                    )}
                  </Box>
                </Box>
              </Paper>
            </Box>
          </Fade>
        </Modal>
      </Container>
    </Page>
  )
}
